import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import { ResponsiveConfirmDialog } from '@/features/marquee/components/responsive-confirm-dialog'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { errorHandler } from '@/helpers/utils'
import { deleteEvent } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

interface DeleteEventDialogProps {
	/** Event to delete via DELETE /events/{id}. */
	eventId: string
	/**
	 * Named in the confirmation so the operator sees which event they are on.
	 * Optional because a screen inside the event may still be loading it; the
	 * control that opens this is a click away, so by then it is there.
	 */
	eventName?: string
	/**
	 * Called once the event is gone, before the cache is refreshed. Passed by a
	 * screen that lives *inside* the event and therefore has to leave it: the
	 * detail query is active there, and refreshing it in place would refetch an
	 * event that now answers 404. With a redirect the list alone is refreshed and
	 * the event's own cache is simply left to go stale; without one (the tracker
	 * list, where nothing about the event is mounted) the whole `events` prefix
	 * is.
	 */
	onDeleted?: () => void
	/**
	 * What this is mounted around, holding the `ResponsiveConfirmTrigger` that
	 * opens it: the delete button, or the whole menu the delete item sits in.
	 */
	children: ReactNode
}

/**
 * "Delete this event?" confirmation, shared by the tracker row menu and the
 * event's own header.
 *
 * The delete is soft server-side (the rows keep a `deleted_at` stamp) but there
 * is no undelete endpoint, so from here it is permanent and the copy says so.
 * It cascades to the attendees, their pitch logs, the event's CTA snapshot and
 * the export-run history, which is why the confirmation names what goes with it
 * rather than only the event.
 *
 * Open state is its own: the trigger opens it and the write closes it, so no
 * caller has a reason to hold it.
 */
function DeleteEventDialog({
	eventId,
	eventName,
	onDeleted,
	children
}: DeleteEventDialogProps) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const [open, setOpen] = useState(false)

	const remove = useMutation({
		mutationFn: () => deleteEvent({ path: { eventId } }),
		onSuccess: async () => {
			setOpen(false)
			// Leave the event before its queries are touched (see `onDeleted`).
			onDeleted?.()
			await queryClient.invalidateQueries({
				queryKey: onDeleted
					? makeQueryKeys.marquee.events.index()
					: makeQueryKeys.marquee.events.all()
			})
		},
		onError: (error) => errorHandler(error)
	})

	return (
		<ResponsiveConfirmDialog
			confirmLabel={t('marquee:events_delete')}
			description={t('marquee:events_delete_dialog_description', {
				name: eventName ?? ''
			})}
			isPending={remove.isPending}
			onConfirm={() => remove.mutate()}
			onOpenChange={setOpen}
			open={open}
			title={t('marquee:events_delete_dialog_title')}
		>
			{children}
		</ResponsiveConfirmDialog>
	)
}

export { DeleteEventDialog }
